/**
 * Webhook Deliveries API Contracts
 *
 * The read surface for the inbound-callback ingestion table
 * (`GET /v1/webhook-deliveries`). Super-admin only, for the same reason the
 * operations timeline is: this is operator-scoped by construction and is never
 * rendered to a school admin.
 *
 * WHY IT EXISTS. `WebhookDelivery` had a writer and a 90-day reaper and no
 * reader at all, so "did App Store Connect actually send us anything, and what
 * did we do with it" was answerable only from a Datadog search that had already
 * rolled off — or from the provider's own console, which for Apple cannot look
 * back more than ten days. The row shape lives with the vocabularies in
 * `schemas/entities/webhook-delivery.ts` and is imported, not redefined.
 *
 * ⚠️ The response deliberately omits `rawPayload`. See the entity schema.
 */

import { z } from 'zod'
import {
  WEBHOOK_DELIVERY_PAGE_SIZE_MAX,
  WebhookDeliveryProviderSchema,
  WebhookDeliverySchema,
  WebhookDeliveryStatusSchema
} from '../../schemas/entities/webhook-delivery'

/**
 * GET /v1/webhook-deliveries — request contract.
 *
 * EVERY ID HERE IS A PUBLIC ID. `resolvedEntityId` on the table is an internal
 * `BigInt` autoincrement; accepting one from a client would be an enumerable
 * direct-object reference, and the reader resolves public ids server-side.
 */
export const ListWebhookDeliveriesRequestSchema = z
  .object({
    provider: WebhookDeliveryProviderSchema.optional(),
    status: WebhookDeliveryStatusSchema.optional(),
    /** The provider's own event-type string, matched exactly. */
    eventType: z.string().min(1).optional(),
    /**
     * Scope to one app's deliveries — `MobileApp.publicId`, resolved to the
     * internal key the column stores.
     */
    mobileAppId: z.string().min(1).optional(),
    /**
     * Only deliveries that resolved to NOTHING. The dead-letter view: a
     * provider sent something genuine that named nothing we know.
     */
    unroutableOnly: z.coerce.boolean().optional(),
    /** Inclusive lower bound on `receivedAt`, ISO-8601. */
    since: z.string().datetime().optional(),
    /** Inclusive upper bound on `receivedAt`, ISO-8601. */
    until: z.string().datetime().optional(),
    limit: z.coerce.number().int().min(1).max(WEBHOOK_DELIVERY_PAGE_SIZE_MAX).optional(),
    /** Public id of the last delivery on the previous page. */
    cursor: z.string().min(1).optional()
  })
  .strict()
export type ListWebhookDeliveriesRequest = z.infer<typeof ListWebhookDeliveriesRequestSchema>

export const ListWebhookDeliveriesResponseSchema = z.object({
  message: z.string(),
  data: z.array(WebhookDeliverySchema),
  nextCursor: z.string().nullable()
})
export type ListWebhookDeliveriesResponse = z.infer<typeof ListWebhookDeliveriesResponseSchema>
